import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { Permission, PermissionProject, PermissionTeam } from 'models';

import { PermissionService } from 'services/PermissionService';

type TeamUserLike = {
    user: CollectionOrId;
    permissions?: readonly string[] | null;
};

type TeamWithJoinedUsers<TeamUser> = {
    users?: { docs?: (string | TeamUser)[] | null } | null;
};

const TEAM_ROLES = [PermissionTeam.OWNER, PermissionTeam.ADMIN, PermissionTeam.USER] as const;
const MANAGER_ROLES = [PermissionTeam.OWNER, PermissionTeam.ADMIN];

const TeamUserHelpers = {
    ownerPermissions: [PermissionTeam.OWNER, PermissionProject.ALL] satisfies Permission[],
    memberPermissions: [PermissionTeam.USER, PermissionProject.ALL] satisfies Permission[],

    fromTeam: <TeamUser extends object>(team?: TeamWithJoinedUsers<TeamUser> | null): TeamUser[] => {
        return (team?.users?.docs ?? []).filter((teamUser): teamUser is TeamUser => typeof teamUser === 'object');
    },

    getPermissions: (teamUser?: TeamUserLike | null) => {
        return (teamUser?.permissions ?? []) as Permission[];
    },

    hasPermission: (teamUser: TeamUserLike | null | undefined, permission: Permission | Permission[]) => {
        return PermissionService.check(TeamUserHelpers.getPermissions(teamUser), permission);
    },

    getRole: (teamUser?: TeamUserLike | null) => {
        const permissions = TeamUserHelpers.getPermissions(teamUser);

        return TEAM_ROLES.find((role) => permissions.includes(role)) ?? null;
    },

    isManager: (teamUser?: TeamUserLike | null) => {
        return TeamUserHelpers.hasPermission(teamUser, MANAGER_ROLES);
    },

    getManagers: <TeamUser extends TeamUserLike>(teamUsers: TeamUser[]) => {
        return teamUsers.filter(TeamUserHelpers.isManager);
    },

    getOwners: <TeamUser extends TeamUserLike>(teamUsers: TeamUser[]) => {
        return teamUsers.filter((teamUser) => TeamUserHelpers.getRole(teamUser) === PermissionTeam.OWNER);
    },

    withRole: <TeamPermission extends string>(permissions: readonly TeamPermission[], role: PermissionTeam) => {
        const withoutRole = permissions.filter((permission) => !isTeamRole(permission));

        return [...withoutRole, role] as (TeamPermission | PermissionTeam)[];
    },
};

const isTeamRole = (permission: string): permission is PermissionTeam => {
    return (TEAM_ROLES as readonly string[]).includes(permission);
};

export type { TeamUserLike };
export { TeamUserHelpers };
